import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import OutlinedFlagIcon from '@material-ui/icons/OutlinedFlag';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { CircularProgress, Snackbar } from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import PersonIcon from '@material-ui/icons/Person';
import NotesIcon from '@material-ui/icons/Notes';
import { flagCall, getVapiCallAnalysis, getVapiCallStatus, getVapiRecordingUrl, releaseCall } from '../../services/vapi.service';
import { bulkReleaseCallAheadOrders } from '../../services';
import Alert from '@material-ui/lab/Alert';
import Transcript from '../../components/CallManager/components/Transcript';
import { Description } from '@material-ui/icons';
import AssignmentIcon from '@material-ui/icons/Assignment';
import VisibilityIcon from '@material-ui/icons/Visibility';
import HeadsetIcon from '@material-ui/icons/Headset';

const styles = theme => ({
  root: {
    padding: 24,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: 24,
  },
  title: {
    color: '#1e5180',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    flex: 1,
    minHeight: 0,
  },
  topSection: {
    display: 'flex',
    gap: 16,
    marginBottom: 16,
    minHeight: 0,
    '& > div': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
  },
  outerContainer: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  innerContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderBottom: '1px solid #e0e0e0',
    paddingLeft: 24,
  },
  iconHeader: {
    padding: '8px 12px',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionHeaderText: {
    fontSize: '20px',
    color: '#1e5180',
    fontWeight: 500,
    textAlign: 'left',
  },
  callInfoContent: {
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
  },
  infoGrid: {
    marginBottom: 24,
  },
  infoLabel: {
    color: '#666',
    fontWeight: 700,
    textAlign: 'left',
  },
  infoValue: {
    color: '#333',
    textAlign: 'left',
  },
  viewOrderButton: {
    backgroundColor: '#315382',
    color: 'white',
    '&:hover': {
      backgroundColor: '#315382',
    },
  },
  releaseButton: {
    backgroundColor: '#4CAF50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  flagButton: {
    backgroundColor: '#ebb20c',
    color: 'white',
    '&:hover': {
      backgroundColor: '#e0a800',
    },
  },
  refreshButton: {
    backgroundColor: '#3a87ad',
    color: 'white',
    '&:hover': {
      backgroundColor: '#2a6d8d',
    },
  },
  backButton: {
    minWidth: 40,
    height: 40,
    padding: 8,
    backgroundColor: '#f5f5f5',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
});

export const CallAnalysis = withStyles(styles)(({ classes }) => {
  const { providerCallId } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [flagging, setFlagging] = useState(false);
  const [callDetails, setCallDetails] = useState(null);
  const [error, setError] = useState(null);
  const [callStatus, setCallStatus] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isFlagged, setIsFlagged] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [isReleased, setIsReleased] = useState(false);

  const fetchCallAnalysis = async () => {
    setLoading(true);
    try {
      const data = await getVapiCallAnalysis(providerCallId);

      const status = await getVapiCallStatus(providerCallId);
      setCallStatus(status || []);

      const allDetails = {
        ...data,
        analysisResults: data.analysisResults ? JSON.parse(data.analysisResults) : null,
      };
      setCallDetails(allDetails);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching call analysis:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleViewOrderCall = async id => {
    history.push(`/orders/${id}`);
  };

  const handleReleaseCall = async id => {
    setIsReleasing(true);
    try {
      await bulkReleaseCallAheadOrders([id]);
      setSnackbar({
        open: true,
        message: `Call released successfully`,
        severity: 'success',
      });
      setIsReleasing(false);
      setIsReleased(true);
    } catch (error) {
      console.error('Error releasing call:', error);
      setSnackbar({
        open: true,
        message: `Error releasing call: ${error.message}`,
        severity: 'error',
      });
      setIsReleasing(false);
    }
  };

  const handleFlagCall = async id => {
    setFlagging(true);
    try {
      await flagCall(id);
      setSnackbar({
        open: true,
        message: `Call flagged successfully`,
        severity: 'success',
      });
      setIsFlagged(true);
      setFlagging(false);
    } catch (error) {
      console.error('Error flagging call:', error);
      setSnackbar({
        open: true,
        message: `Error flagging call: ${error.message}`,
        severity: 'error',
      });
      setFlagging(false);
    }
  };

  useEffect(() => {
    fetchCallAnalysis();
  }, [providerCallId]);

  const handleBack = () => {
    history.goBack();
  };

  const handleRefresh = () => {
    fetchCallAnalysis();
  };

  if (error) {
    return (
      <div className={classes.root}>
        <Typography color="error" align="center">
          {error}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleSection}>
          <Button className={classes.backButton} style={{ marginRight: 16 }} variant="contained" onClick={handleBack}>
            <ArrowBackIcon />
            Back
          </Button>
          {loading || !callDetails ? (
            <div>
              <CircularProgress />
            </div>
          ) : (
            <Typography variant="h4" className={classes.title}>
              {`${callDetails?.customer.fname} ${callDetails?.customer.lname}`}
            </Typography>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            variant="contained"
            className={classes.releaseButton}
            startIcon={<CheckCircleOutlineIcon />}
            onClick={() => handleReleaseCall(callDetails?.orderLineId)}
            disabled={loading || isReleasing || callDetails?.orderLineStatusLookupId === 2 || isReleased}
          >
            {isReleasing
              ? 'Releasing...'
              : callDetails?.orderLineStatusLookupId === 2 || isReleased
              ? 'Assigned'
              : 'Release'}
          </Button>
          {/* <Button
            variant="contained"
            className={classes.flagButton}
            startIcon={<OutlinedFlagIcon />}
            onClick={() => handleFlagCall(callDetails?.callLogId)}
            disabled={loading || flagging || callDetails?.isFlagged || isFlagged}
          >
            {flagging ? 'Flagging...' : callDetails?.isFlagged || isFlagged ? 'Flagged' : 'Flag'}
          </Button> */}
          <Button
            variant="contained"
            className={classes.refreshButton}
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className={classes.content}>
        <div className={classes.topSection}>
          <div>
            <div style={{ marginRight: 8 }} className={classes.outerContainer}>
              <Paper className={classes.innerContainer} elevation={0}>
                <Grid xs={12} container spacing={3} style={{ flexBasis: 0 }}>
                  <Grid item xs={6}>
                    <div className={classes.iconContainer} style={{ paddingLeft: 8 }}>
                      <div className={classes.iconHeader} style={{ gap: 8 }}>
                        <PhoneIcon />
                        <Typography className={classes.sectionHeaderText}>Call Outcome</Typography>
                      </div>
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className={classes.iconContainer} style={{ paddingLeft: 8 }}>
                      <div className={classes.iconHeader} style={{ gap: 8 }}>
                        <PersonIcon />
                        <Typography className={classes.sectionHeaderText}>Customer Interaction</Typography>
                      </div>
                    </div>
                  </Grid>
                </Grid>
                {loading || !callDetails ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress />
                  </div>
                ) : (
                  <div className={classes.callInfoContent}>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <Grid container spacing={2} className={classes.infoGrid}>
                          <Grid item xs={12}>
                            <Typography
                              className={classes.infoLabel}
                              style={{
                                color:
                                  callDetails.analysisResults?.structuredData?.MESSAGE_COMPLETED === 'true'
                                    ? '#2e7d32'
                                    : '#d32f2f',
                              }}
                            >
                              Message Completed:{' '}
                              {callDetails.analysisResults?.structuredData?.MESSAGE_COMPLETED === 'true'
                                ? 'Yes'
                                : callDetails.analysisResults?.structuredData?.MESSAGE_COMPLETED === 'false'
                                ? 'No'
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography className={classes.infoLabel}>
                              Voicemail Left:{' '}
                              {callDetails.analysisResults?.structuredData?.VOICEMAIL_LEFT === 'true' ? 'Yes' : 'No'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography className={classes.infoLabel}>
                              Access Restrictions:{' '}
                              {callDetails.analysisResults?.structuredData?.ACCESS_RESTRICTIONS === 'true'
                                ? 'Yes'
                                : callDetails.analysisResults?.structuredData?.ACCESS_RESTRICTIONS === 'false'
                                ? 'No'
                                : 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography className={classes.infoLabel} style={{ color: '#F0AD4E' }}>
                              Other Concern: {callDetails.analysisResults?.structuredData?.OTHER_CONCERNS || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography className={classes.infoLabel} style={{ marginBottom: '8px' }}>
                          Caller Mood
                        </Typography>
                        <Typography className={classes.infoValue} style={{ marginBottom: '16px' }}>
                          {callDetails.analysisResults?.structuredData?.CALLER_MOOD || 'No data available'}
                        </Typography>
                        <Typography className={classes.infoLabel} style={{ marginBottom: '8px' }}>
                          Questions Asked
                        </Typography>
                        {callDetails.analysisResults?.structuredData?.QUESTIONS_ASKED?.length > 0 ? (
                          callDetails.analysisResults?.structuredData?.QUESTIONS_ASKED?.map((question, index) => (
                            <Typography key={index} className={classes.infoValue}>
                              {question}
                            </Typography>
                          ))
                        ) : (
                          <Typography className={classes.infoValue}>No data available</Typography>
                        )}
                      </Grid>
                    </Grid>
                  </div>
                )}
                <Grid item xs={12}>
                  <div className={classes.iconContainer} style={{ paddingLeft: 8 }}>
                    <div className={classes.iconHeader} style={{ gap: 8 }}>
                      <AssignmentIcon />
                      <Typography className={classes.sectionHeaderText}>Order Information</Typography>
                    </div>
                  </div>
                  {loading || !callDetails ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <CircularProgress />
                    </div>
                  ) : (
                    <Grid container spacing={2} style={{ padding: '16px 24px' }}>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Order Number</Typography>
                        <Typography className={classes.infoValue}>{callDetails?.orderLineId || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography className={classes.infoLabel}>Policy Number</Typography>
                        <Typography className={classes.infoValue}>{callDetails?.policyNumber || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Button
                          variant="contained"
                          className={classes.viewOrderButton}
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewOrderCall(callDetails?.orderLineId)}
                          disabled={
                            loading || 
                            !callDetails?.orderLineId || 
                            callDetails?.orderLineStatusLookupId === 2 ||
                            callDetails?.orderStateId === 4 // OrderStateId: 4 = CANCELLED
                          }
                        >
                          View Order
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </div>
          </div>
          <div>
            <div style={{ marginLeft: 8 }} className={classes.outerContainer}>
              <Paper className={classes.innerContainer} elevation={0}>
                <div className={classes.iconContainer}>
                  <Description />
                  <div className={classes.iconHeader}>
                    <Typography className={classes.sectionHeaderText}>Call Script</Typography>
                  </div>
                </div>
                {loading || !callDetails ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress />
                  </div>
                ) : (
                  <div className={classes.callInfoContent} style={{ textAlign: 'left' }}>
                    <Typography>{callStatus?.assistantOverrides.voicemailMessage || 'No data available'}</Typography>
                  </div>
                )}
              </Paper>
            </div>
          </div>
        </div>
        <div className={classes.topSection}>
          <div>
            <div style={{ marginRight: 8 }} className={classes.outerContainer}>
              <Paper className={classes.innerContainer} elevation={0}>
                <div className={classes.iconContainer}>
                  <NotesIcon />
                  <div className={classes.iconHeader}>
                    <Typography className={classes.sectionHeaderText}>Call Summary</Typography>
                  </div>
                </div>
                {loading || !callDetails ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <CircularProgress />
                  </div>
                ) : (
                  <div className={classes.callInfoContent} style={{ textAlign: 'left' }}>
                    <Typography>{callDetails?.callSummary || 'No data available'}</Typography>
                  </div>
                )}
                {callDetails?.hasRecording && (
                  <>
                    <div className={classes.iconContainer} style={{ paddingLeft: 8 }}>
                      <div className={classes.iconHeader} style={{ gap: 8 }}>
                        <HeadsetIcon />
                        <Typography className={classes.sectionHeaderText}>Call Recording</Typography>
                      </div>
                    </div>
                    <div style={{ padding: '16px 24px' }}>
                      <audio controls style={{ width: '100%' }} src={getVapiRecordingUrl(providerCallId)}>
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </>
                )}
              </Paper>
            </div>
          </div>
          <div>
            <div style={{ marginLeft: 8, height: '400px' }} className={classes.outerContainer}>
              <Paper className={classes.innerContainer} elevation={0}>
                <Transcript loading={loading} transcript={callStatus?.messages} />
              </Paper>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
});

export default CallAnalysis;
